import type { BudgetDocument, BudgetLine, BudgetTotals } from '../types'
import { roundMoney } from '../utils'

/**
 * Deterministic totals calculator — single source of truth for money math.
 */
export class BudgetCalculator {
  lineNet(line: BudgetLine): number {
    const gross = line.quantity * line.unitPrice
    const percentDiscount = gross * (line.discountPercent / 100)
    return roundMoney(Math.max(0, gross - percentDiscount - line.discountValue))
  }

  lineCost(line: BudgetLine): number {
    return roundMoney(line.quantity * line.unitCost)
  }

  recompute(document: BudgetDocument): BudgetDocument {
    const lines = [...document.lines]
      .sort((a, b) => a.position - b.position)
      .map((line, index) => ({ ...line, position: index }))

    const totals = this.totals(lines, {
      globalDiscountPercent: document.globalDiscountPercent,
      globalDiscountValue: document.globalDiscountValue,
      shipping: document.shipping,
      taxRatePercent: document.taxRatePercent,
    })

    return {
      ...document,
      lines,
      totals,
      updatedAt: document.updatedAt,
    }
  }

  totals(
    lines: BudgetLine[],
    options: {
      globalDiscountPercent: number
      globalDiscountValue: number
      shipping: number
      taxRatePercent: number
    },
  ): BudgetTotals {
    const quantity = lines.reduce((acc, line) => acc + line.quantity, 0)
    const costTotal = roundMoney(lines.reduce((acc, line) => acc + this.lineCost(line), 0))
    const subtotal = roundMoney(lines.reduce((acc, line) => acc + this.lineNet(line), 0))

    const percentDiscount = subtotal * (options.globalDiscountPercent / 100)
    const discountTotal = roundMoney(
      Math.min(subtotal, percentDiscount + Math.max(0, options.globalDiscountValue)),
    )
    const afterDiscount = roundMoney(Math.max(0, subtotal - discountTotal))
    const shipping = roundMoney(Math.max(0, options.shipping))
    const taxes = roundMoney(afterDiscount * (Math.max(0, options.taxRatePercent) / 100))
    const total = roundMoney(afterDiscount + shipping + taxes)
    const profit = roundMoney(total - costTotal - shipping - taxes)
    const marginPercent =
      total > 0 ? roundMoney((profit / total) * 100) : 0

    return {
      quantity,
      costTotal,
      subtotal,
      discountTotal,
      shipping,
      taxes,
      total,
      profit,
      marginPercent,
    }
  }
}

export const budgetCalculator = new BudgetCalculator()
